import { AdminPlatform } from "@/design/AdminPlatform";
import { DataUnavailable } from "@/design/DataUnavailable";
import {
  RegistrationCard,
  type RegistrationCardData,
} from "@/design/RegistrationCard";
import { PageShell } from "@/design/ui";
import { formStateFor as onboardingStateFor } from "@/modules/onboarding/commands/onboardingCopy";
import type {
  PlatformBusiness,
  PlatformConsumer,
  PlatformTotals,
} from "@/modules/insights/surfaces";
import { AdminPage } from "~/components/BusinessChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { apiFormAction } from "~/screens/business/SettingsToolScreens";
import { useQuery } from "~/lib/useQuery";

/**
 * The admin console (R22 T15), two screens: the queue of businesses waiting to
 * be let onto Bookr, and the platform view.
 *
 * **Two honest sections, not three broken ones.** The admin area used to mount
 * the *business* navigation, which offered Schedule, Dashboard and Settings,
 * all pointing at `/business` routes an admin's role cannot open.
 */

// ── The queue (docs/ux/screens/AdminRegistrationQueueScreen.md) ──────────────

interface QueuePayload {
  registrations: RegistrationCardData[];
}

/**
 * **The count belongs in the heading**, because the queue is the whole page: an
 * admin opening this wants to know how many decisions are waiting before they
 * read a single card.
 *
 * A decision removes a card, so the queue re-reads after either. Approval
 * creates the Business and rejection deletes the registration (H9), and neither
 * is undoable from here.
 */
export function AdminRegistrationQueueScreen() {
  const queue = useQuery<QueuePayload>("/admin/registrations");
  const reload = () => queue.reload();
  const cards = queue.data?.registrations ?? null;

  return (
    <AdminPage>
      <PageShell>
        <div className="mx-auto w-full max-w-2xl">
          <header className="mb-6">
            <h1 className="text-2xl font-bold tracking-tight text-ink">
              Registrations
            </h1>
            <p className="mt-1 text-sm text-muted">
              {cards === null
                ? "Businesses waiting to be let onto Bookr."
                : cards.length === 0
                  ? "Nothing is waiting. Businesses appear here as they apply."
                  : `${cards.length} ${cards.length === 1 ? "business is" : "businesses are"} waiting for a decision.`}
            </p>
          </header>

          {queue.loading && !cards ? (
            <ScreenPending />
          ) : queue.failure || cards === null ? (
            <DataUnavailable
              configured={queue.failure !== "not_configured"}
              what="the registration queue"
            />
          ) : cards.length === 0 ? (
            <p className="rounded-2xl border border-line bg-surface p-10 text-center text-sm text-muted">
              No pending registrations.
            </p>
          ) : (
            <div className="space-y-4">
              {cards.map((registration) => (
                <RegistrationCard
                  key={registration.id}
                  registration={registration}
                  commands={{
                    approve: apiFormAction({
                      method: "POST",
                      path: (form) =>
                        `/admin/registrations/${String(
                          form.get("registrationId") ?? "",
                        )}/approve`,
                      render: onboardingStateFor,
                      onSuccess: reload,
                    }),
                    reject: apiFormAction({
                      method: "POST",
                      path: (form) =>
                        `/admin/registrations/${String(
                          form.get("registrationId") ?? "",
                        )}/reject`,
                      render: onboardingStateFor,
                      onSuccess: reload,
                    }),
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </PageShell>
    </AdminPage>
  );
}

// ── The platform view (docs/ux/screens/AdminPlatformScreen.md) ───────────────

interface PlatformPayload {
  totals: PlatformTotals;
  businesses: PlatformBusiness[];
  consumers: PlatformConsumer[];
}

export function AdminPlatformScreen() {
  const platform = useQuery<PlatformPayload>("/admin/insights");

  return (
    <AdminPage>
      <PageShell>
        <div className="mx-auto w-full max-w-4xl">
          {/* The heading belongs to the shared component, which both hosts
              render; a second one here would announce the screen twice. */}
          {platform.loading && !platform.data ? (
            <ScreenPending />
          ) : (
            <AdminPlatform
              totals={platform.data?.totals ?? null}
              businesses={platform.data?.businesses ?? []}
              consumers={platform.data?.consumers ?? []}
              configured={platform.failure !== "not_configured"}
            />
          )}
        </div>
      </PageShell>
    </AdminPage>
  );
}
